import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import XIcon from '../../../../assets/icons/XIcon';
import type {ChatAskPrompt} from '../../../aiMessageGenerator/utils/types';
import AiAgentAskCompose from './AiAgentAskCompose';
import * as css from './AiAgentAsk.css';

interface AiAgentAskProps extends ChatAskPrompt {
    draftLoading?: boolean;
    onSelect: (value: string, label?: string) => void;
    onDismiss: () => void;
}

function AiAgentAsk({
    options,
    question,
    draftLoading = false,
    onSelect,
    onDismiss,
}: AiAgentAskProps) {
    const {t} = useTranslation('aiAgent');

    const handleOptionClick = useCallback((value: string, label: string) => {
        if(draftLoading) {
            return;
        }
        onSelect(value, label);
    }, [draftLoading, onSelect]);

    return (
        <>
            <css.askBeamStyles />
            <css.card>
                <css.head>
                    <css.question>{question}</css.question>
                    <css.closeButton
                        type="button"
                        onClick={onDismiss}
                        aria-label={t('dismissAsk')}
                        data-cy="ai-agent-ask-dismiss"
                    >
                        <XIcon color="currentColor" width={18} height={18} />
                    </css.closeButton>
                </css.head>
                <css.optionsList>
                    {options.map((option, index) => (
                        <css.option
                            key={`${option.value}-${index}`}
                            type="button"
                            disabled={draftLoading}
                            onClick={() => handleOptionClick(option.value, option.label)}
                            data-cy={`ai-agent-ask-option-${index}`}
                        >
                            <css.optionNumber>{option.index ?? index + 1}</css.optionNumber>
                            <css.optionLabel>{option.label}</css.optionLabel>
                        </css.option>
                    ))}
                </css.optionsList>
                <AiAgentAskCompose disabled={draftLoading} onSubmit={onSelect} />
            </css.card>
        </>
    );
}

export default AiAgentAsk;
